import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { SocialPlatform, ConnectSocialAccountPayload } from '../../../../common/models/social-scheduler.model';
import { useSocialSchedulerApi } from '../../../../common/hooks/use-social-scheduler-api.hook';
import { PlatformIcon } from './PlatformIcon';

interface ConnectAccountDialogProps {
    open: boolean;
    onClose: () => void;
    onConnect: (payload: ConnectSocialAccountPayload) => Promise<void>;
    /** Pre-select a platform when opening the dialog */
    initialPlatform?: SocialPlatform;
}

// Only platforms backed by a real publishing provider are exposed.
// TikTok / LinkedIn will be added back when their OAuth flow + Content APIs ship.
const platforms: { value: SocialPlatform; label: string }[] = [
    { value: 'instagram', label: 'Instagram' },
    { value: 'meta', label: 'Meta (Facebook)' },
];

/** Three identical label/input pairs sat here; one shape keeps them aligned. */
const Field = ({
    id,
    label,
    ...inputProps
}: { id: string; label: string } & React.ComponentProps<typeof Input>) => (
    <div className="flex flex-col gap-1.5">
        <Label htmlFor={id}>{label}</Label>
        <Input id={id} {...inputProps} />
    </div>
);

export const ConnectAccountDialog: React.FC<ConnectAccountDialogProps> = ({
    open,
    onClose,
    onConnect,
    initialPlatform,
}) => {
    const { t } = useTranslation();
    const api = useSocialSchedulerApi();

    const [platform, setPlatform] = useState<SocialPlatform>(initialPlatform || 'instagram');
    const [accountId, setAccountId] = useState('');
    const [accountName, setAccountName] = useState('');
    const [accessToken, setAccessToken] = useState('');
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Meta OAuth: check configuration on open
    const [metaOAuthConfigured, setMetaOAuthConfigured] = useState<boolean | null>(null);
    const [oauthRedirecting, setOauthRedirecting] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (initialPlatform) setPlatform(initialPlatform);
        // Fetch OAuth status once per open
        (async () => {
            try {
                const res = await api.getMetaOAuthStatus();
                setMetaOAuthConfigured(!!res.configured);
            } catch {
                setMetaOAuthConfigured(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialPlatform]);

    const isMetaPlatform = platform === 'meta' || platform === 'instagram';
    const useOAuth = isMetaPlatform && metaOAuthConfigured === true;

    const canSubmit = !useOAuth && platform && accountId.trim() && accountName.trim() && accessToken.trim();

    const handleManualSubmit = async () => {
        if (!canSubmit) return;
        setLoading(true);
        setErrorMessage(null);
        try {
            await onConnect({ platform, accountId, accountName, accessToken });
            setPlatform(initialPlatform || 'instagram');
            setAccountId('');
            setAccountName('');
            setAccessToken('');
            onClose();
        } catch (err: any) {
            setErrorMessage(err?.message || t('SOCIAL_SCHEDULER.CONNECT_FAILED', 'Could not connect this account. Check your credentials and try again.'));
        } finally {
            setLoading(false);
        }
    };

    const handleOAuthStart = async () => {
        setOauthRedirecting(true);
        setErrorMessage(null);
        try {
            const { authUrl } = await api.startMetaOAuth();
            window.location.href = authUrl;
        } catch (err: any) {
            console.error('Failed to start OAuth:', err);
            setOauthRedirecting(false);
            setErrorMessage(err?.message || t('SOCIAL_SCHEDULER.OAUTH_START_FAILED', 'Could not start the Facebook authorization flow. Please try again.'));
        }
    };

    const handleClose = () => {
        setErrorMessage(null);
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={(next) => { if (!next) handleClose(); }}>
            <DialogContent className="sm:max-w-[480px]">
                <DialogHeader>
                    <DialogTitle>{t('SOCIAL_SCHEDULER.CONNECT_ACCOUNT', 'Connect account')}</DialogTitle>
                </DialogHeader>

                <div className="flex flex-col gap-4">
                    {errorMessage && (
                        <Alert variant="destructive">
                            <AlertDescription>{errorMessage}</AlertDescription>
                        </Alert>
                    )}

                    <div className="flex flex-col gap-1.5">
                        <Label>{t('SOCIAL_SCHEDULER.PLATFORM', 'Platform')}</Label>
                        <div className="flex flex-wrap gap-2">
                            {platforms.map((p) => {
                                const isSelected = platform === p.value;
                                return (
                                    <button
                                        key={p.value}
                                        type="button"
                                        onClick={() => setPlatform(p.value)}
                                        aria-pressed={isSelected}
                                        className={cn(
                                            'inline-flex items-center gap-1.5 rounded-lg border px-4 py-2 text-sm font-medium transition-colors outline-none',
                                            'focus-visible:ring-ring/50 focus-visible:ring-[3px]',
                                            isSelected
                                                ? 'border-foreground bg-accent text-foreground'
                                                : 'border-input bg-background text-foreground hover:bg-accent',
                                        )}
                                    >
                                        <PlatformIcon platform={p.value} size={18} />
                                        {p.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* ── Meta OAuth path ───────────────────────── */}
                    {useOAuth && (
                        <div className="rounded-xl border bg-muted p-5 text-center">
                            <div className="flex justify-center">
                                <PlatformIcon platform="meta" size={48} />
                            </div>
                            <p className="mt-3 mb-1 text-sm font-medium text-foreground">
                                {t('SOCIAL_SCHEDULER.OAUTH_META_TITLE', 'Connect with Facebook')}
                            </p>
                            <p className="mx-auto mb-5 max-w-[380px] text-sm text-muted-foreground">
                                {t(
                                    'SOCIAL_SCHEDULER.OAUTH_META_DESC',
                                    'You’ll be redirected to Facebook to authorize Ardealy. We’ll connect every Facebook Page and linked Instagram Business account you select.',
                                )}
                            </p>
                            {/*
                             * Facebook's blue is the one colour here that is not ours to
                             * restyle — it is how a person recognises the channel they are
                             * about to hand credentials to.
                             */}
                            <Button
                                onClick={handleOAuthStart}
                                size="lg"
                                disabled={oauthRedirecting}
                                className="text-white hover:opacity-90"
                                style={{ backgroundColor: '#1877F2' }}
                            >
                                {oauthRedirecting && <Loader2 className="size-4 animate-spin" />}
                                {oauthRedirecting
                                    ? t('SOCIAL_SCHEDULER.OAUTH_REDIRECTING', 'Redirecting to Facebook…')
                                    : t('SOCIAL_SCHEDULER.OAUTH_CONTINUE_META', 'Continue with Facebook')}
                            </Button>
                        </div>
                    )}

                    {/* ── Manual token path (non-Meta or OAuth not configured) ── */}
                    {!useOAuth && (
                        <>
                            {isMetaPlatform && metaOAuthConfigured === false && (
                                <div className="rounded-lg border border-warning/40 bg-warning/10 p-3 text-xs text-foreground">
                                    {t(
                                        'SOCIAL_SCHEDULER.OAUTH_NOT_CONFIGURED',
                                        'Meta OAuth is not configured on this server. Paste an access token manually to connect for testing.',
                                    )}
                                </div>
                            )}
                            <Field
                                id="ss-account-id"
                                label={t('SOCIAL_SCHEDULER.ACCOUNT_ID', 'Account ID')}
                                value={accountId}
                                onChange={(e) => setAccountId(e.target.value)}
                                placeholder="e.g. 17841400123456789"
                            />
                            <Field
                                id="ss-account-name"
                                label={t('SOCIAL_SCHEDULER.ACCOUNT_NAME', 'Account name')}
                                value={accountName}
                                onChange={(e) => setAccountName(e.target.value)}
                                placeholder="e.g. @mybrand"
                            />
                            <Field
                                id="ss-access-token"
                                label={t('SOCIAL_SCHEDULER.ACCESS_TOKEN', 'Access token')}
                                type="password"
                                value={accessToken}
                                onChange={(e) => setAccessToken(e.target.value)}
                            />
                        </>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" className="h-9" onClick={handleClose}>
                        {t('SOCIAL_SCHEDULER.CANCEL', 'Cancel')}
                    </Button>
                    {!useOAuth && (
                        <Button className="h-9" onClick={handleManualSubmit} disabled={!canSubmit || loading}>
                            {loading
                                ? t('SOCIAL_SCHEDULER.CONNECTING', 'Connecting...')
                                : t('SOCIAL_SCHEDULER.CONNECT', 'Connect')}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
